import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ease = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay = 0, className = "", active = false }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      initial={{ y: "112%", rotate: 1.5 }}
      animate={active ? { y: "0%", rotate: 0 } : { y: "112%", rotate: 1.5 }}
      transition={{ duration: 0.9, delay, ease }}
      className={`block origin-left will-change-transform ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Chapter = ({ index, title, description }) => (
  <motion.article
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.75, delay: 0.12 * index, ease }}
    className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.035] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm transition-colors duration-500 hover:border-miles-electric/35 hover:bg-white/[0.06] sm:p-7"
    data-testid={`manifesto-chapter-${index + 1}`}
  >
    <div className="pointer-events-none absolute -right-24 -top-24 h-52 w-52 rounded-full bg-miles/20 opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-100" />
    <div className="relative grid gap-5 sm:grid-cols-[72px_1fr_auto] sm:items-start">
      <p className="font-mono text-sm font-semibold tracking-[0.24em] text-miles-electric">
        {String(index + 1).padStart(2, "0")}
      </p>
      <div>
        <h3 className="text-2xl font-semibold tracking-[-0.03em] text-white sm:text-3xl">{title}</h3>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-miles-sky/65 transition-colors duration-500 group-hover:text-miles-sky/90 sm:text-base">
          {description}
        </p>
      </div>
      <span className="hidden h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white/35 transition-[border-color,color,transform] duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:border-miles-electric/50 group-hover:text-white sm:flex">
        <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
      </span>
    </div>
    <span className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-miles-electric transition-transform duration-700 ease-out group-hover:scale-x-100" />
  </motion.article>
);

export default function ManifestoSection() {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true, amount: 0.15 });
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [-34, 34]);
  const photoScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.12, 1.03, 1.08]);
  const glowOpacity = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [0.25, 0.8, 0.25]);

  return (
    <section
      ref={sectionRef}
      className="grain relative isolate overflow-hidden bg-miles-deep py-16 text-white sm:py-20 lg:py-24"
      data-testid="manifesto-section"
    >
      <motion.div
        style={{ opacity: glowOpacity }}
        className="pointer-events-none absolute -left-48 top-20 h-[34rem] w-[34rem] rounded-full bg-miles/25 blur-[120px]"
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-miles-electric/50 to-transparent" />
      <svg className="pointer-events-none absolute right-[-12rem] top-16 hidden h-[42rem] w-[52rem] opacity-25 lg:block" viewBox="0 0 820 680" fill="none" aria-hidden="true">
        <motion.path
          d="M-20 590C142 488 224 532 344 404C476 263 576 343 840 106"
          stroke="#56B8FF"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeDasharray="7 11"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 2.4, ease }}
        />
      </svg>

      <div className="relative mx-auto max-w-[96rem] px-5 sm:px-8 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-[1.45fr_0.55fr] lg:items-start lg:gap-20">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, ease }}
              className="font-mono text-[11px] uppercase tracking-[0.35em] text-miles-electric"
            >
              {t("manifesto.eyebrow")}
            </motion.p>
            <h2 ref={titleRef} className="mt-7 max-w-6xl text-[clamp(3.15rem,7.6vw,7.75rem)] font-semibold leading-[0.9] tracking-[-0.07em]" data-testid="manifesto-title" aria-label={`${t("manifesto.t1")} ${t("manifesto.t2")}`}>
              <span aria-hidden="true">
                <MaskedLine active={titleInView} delay={0.05}>{t("manifesto.t1")}</MaskedLine>
                <MaskedLine active={titleInView} delay={0.17} className="font-light text-miles-sky/50">
                  {t("manifesto.t2")}
                </MaskedLine>
              </span>
            </h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.24, ease }}
            className="max-w-md lg:justify-self-end lg:pt-16"
          >
            <p className="border-l border-miles-electric/40 pl-6 text-base leading-relaxed text-miles-sky/70 sm:text-lg">
              {t("manifesto.p")}
            </p>
            <Link
              to="/suivi"
              data-testid="manifesto-system-link"
              className="group mt-8 inline-flex items-center gap-3 rounded-full border border-white/10 px-6 py-3 text-sm font-semibold text-white transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-miles-electric/60 hover:bg-white/[0.05]"
            >
              {t("manifesto.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>

        <div className="mt-14 grid gap-10 lg:mt-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 xl:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease }}
            className="relative"
            data-testid="manifesto-photo-spotlight"
          >
            <div className="absolute -left-4 top-10 z-10 hidden -rotate-90 font-mono text-[10px] uppercase tracking-[0.4em] text-white/35 lg:block">
              MILES / 01
            </div>
            <div className="relative rounded-[2.25rem] border border-white/10 bg-white/[0.045] p-3 shadow-[0_56px_140px_-48px_rgba(0,0,0,0.78)] backdrop-blur-md">
              <div className="relative h-[430px] overflow-hidden rounded-[1.7rem] sm:h-[560px] lg:h-[640px]">
                <motion.img
                  src="/hero-miles.webp"
                  alt={t("manifesto.photo_alt")}
                  loading="lazy"
                  style={{ y: photoY, scale: photoScale }}
                  className="img-clean h-[112%] w-full object-cover object-[62%_center] will-change-transform"
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_52%_38%,transparent_0%,rgba(11,31,77,0.06)_34%,rgba(11,31,77,0.68)_100%)]" />
                <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-miles-deep/90 to-transparent" />
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.45, ease }}
                  className="absolute bottom-5 left-5 right-5 rounded-3xl border border-white/10 bg-miles-deep/60 p-5 shadow-[0_24px_70px_rgba(0,0,0,0.36)] backdrop-blur-xl sm:bottom-7 sm:left-7 sm:right-auto sm:w-[24rem] sm:p-6"
                  data-testid="manifesto-live-card"
                >
                  <div className="flex items-center justify-between gap-4">
                    <p className="flex items-center gap-2.5 text-sm font-semibold text-white">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-miles text-white">
                        <Check className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      {t("manifesto.photo_status")}
                    </p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-miles-sky/60">08:15</span>
                  </div>
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <p className="flex items-start gap-2.5 text-sm leading-relaxed text-white/80">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-miles-electric" strokeWidth={1.5} />
                      {t("manifesto.photo_label")}
                    </p>
                    <div className="mt-4 flex items-center justify-between gap-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-miles-sky/55">{t("manifesto.card_note")}</p>
                      <p className="whitespace-nowrap font-mono text-sm font-semibold text-white">{t("manifesto.photo_price")}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <div className="flex flex-col justify-center gap-4 lg:gap-5" data-testid="manifesto-chapters">
            {t("manifesto.chapters").map(([title, description], index) => (
              <Chapter key={title} index={index} title={title} description={description} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
